import React, { useState, useEffect } from "react";
import {
  Home,
  CircleDollarSign,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import authApi from "../api/authApi";
import finzSmall from '../assets/sidebar/finz-small.png';
import finzFull from '../assets/sidebar/finz-full.png';

interface SidebarProps {
  onCollapseChange: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [hoveringLogo, setHoveringLogo] = useState(false);

  const handleLogout = () => {
    authApi.logout();
    navigate('/');
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapseChange(next);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 850) {
        setCollapsed(true);
        onCollapseChange(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navItems = [
    { icon: <Home size={19} />, title: "Home", to: "/home" },
    { icon: <CircleDollarSign size={19} />, title: "Deals", to: "/deals" },
  ];

  return (
    <div
      className={`justify-between fixed top-0 left-0 bg-gray-800 text-white transition-all duration-300 z-50 flex flex-col 
      overflow-y-auto overflow-x-hidden h-[100dvh] 
      ${collapsed ? 'w-[65px]' : 'w-[200px]'}`}
    >
      <div className="flex flex-col">
        {/* Logo + Collapse */}
        <div className={`px-5 flex items-center justify-between pt-4 h-12 relative`}>
          <div className="relative flex items-center w-full">
            {/* Full logo */}
            <NavLink to="/home">
              {!collapsed && (
                <img
                  src={finzFull}
                  alt="logo"
                  className="h-auto"
                />
              )}
            </NavLink>

            <div
              className="absolute left-0 w-6"
              onMouseEnter={() => setHoveringLogo(true)}
              onMouseLeave={() => setHoveringLogo(false)}>
              {/* Small logo (only when collapsed and NOT hovering) */}
              <img
                src={finzSmall}
                alt="logo"
                className={`${collapsed && !hoveringLogo ? "block" : "hidden"}`}
              />

              {/* Chevron right (only when collapsed and hovering) */}
              <button
                onClick={toggleCollapse}
                className={`${collapsed && hoveringLogo ? "block" : "hidden"}`}
              >
                <ChevronsRight className="text-white hover:text-indigo-300" size={20} />
              </button>
            </div>
          </div>

          {/* Chevron left (collapse) */}
          {!collapsed &&
            <button onClick={toggleCollapse}>
              <ChevronsLeft className="text-white hover:text-indigo-300" size={20} />
            </button>
          }
        </div>

        {/* Navigation */}
        <nav className="flex flex-col mt-16">
          {navItems.map(({ icon, title, to }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 py-2 font-medium transition-all duration-200 
              ${isActive ? "bg-gray-700" : "hover:bg-gray-700"} px-5`}
            >
              {({ isActive }) => (
                <>
                  <div className={`p-1 ${isActive ? "text-indigo-400" : "text-white"}`}>
                    {icon}
                  </div>
                  {!collapsed && <span className="text-base">{title}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Logout - Bottom */}
      <div className="flex flex-col mb-5">
        <button
          onClick={handleLogout}
          className="px-6 font-medium flex items-center gap-3 py-2 hover:bg-gray-700 transition-all duration-200"
        >
          <div className="text-white" title="Logout">
            <LogOut size={18} />
          </div>
          <span
            className={`text-base transition-opacity duration-200 ${collapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100 w-auto"
              }`}
          >
            Logout
          </span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
